/**
 * The scene editor's Publish button and badge (spec 011): a free scene
 * publishes as a playable item of its own. The badge reads "Not published",
 * "Published vN · <date>", or "Files missing on this server — publish again"
 * when this server lacks the version's files; "Open ↗" opens `playerUrl`.
 * Every URL comes from the Service; nothing is assembled here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { useSceneStore } from '../stores/sceneStore';
import type { PublishedSceneStatus } from '../../../Shared/src/@types';
import styles from './ScenePublishControl.module.scss';

type Status = Pick<PublishedSceneStatus, 'version' | 'publishedAt' | 'playerUrl' | 'filesPresent'>;

function formatPublishedAt(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const ScenePublishControl: React.FC<{ sceneId: number; buttonClassName?: string; disabled?: boolean }> = ({
  sceneId,
  buttonClassName,
  disabled,
}) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setStatus(null);
    apiClient.getPublishedScene(sceneId).then((res) => {
      if (live && res.success && res.data) setStatus(res.data);
    }).catch(() => { /* the badge stays "Not published" */ });
    return () => { live = false; };
  }, [sceneId]);

  const publish = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const store = useSceneStore.getState();
      if (store.isDirty || store.saveStatus === 'saving') {
        await store.saveScene();
        const after = useSceneStore.getState();
        if (after.isDirty || after.saveStatus === 'error') {
          setError('Your changes could not be saved, so nothing was published.');
          return;
        }
      }
      const res = await apiClient.publishScene(sceneId);
      if (res.success) {
        setStatus({ version: res.data.version, publishedAt: res.data.publishedAt, playerUrl: res.data.playerUrl, filesPresent: true });
      } else {
        const failure = res as { error?: string; missingMediaIds?: number[] };
        setError(failure.missingMediaIds?.length
          ? `Missing media files (${failure.missingMediaIds.join(', ')})`
          : failure.error ?? 'Publish failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed');
    } finally {
      setBusy(false);
    }
  }, [busy, sceneId]);

  return (
    <div className={styles.root}>
      <span className={styles.badge} role="status" data-testid="scene-publish-badge">
        {!status
          ? 'Not published'
          : status.filesPresent
            ? `Published v${status.version} · ${formatPublishedAt(status.publishedAt)}`
            : 'Files missing on this server — publish again'}
      </span>
      {status?.filesPresent && (
        <a
          className={styles.badge}
          href={status.playerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="publish-player-link"
          title="Open the published scene in a new tab"
        >
          Open ↗
        </a>
      )}
      {error && <span className={styles.error} role="alert" data-testid="scene-publish-error">{error}</span>}
      <button
        type="button"
        className={buttonClassName}
        onClick={() => void publish()}
        disabled={busy || disabled}
        data-testid="scene-publish"
        title="Publish this scene as a playable item of its own"
      >
        {busy ? 'Publishing…' : 'Publish'}
      </button>
    </div>
  );
};

export default ScenePublishControl;
